import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ACCEPTED_FILE_TYPES, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { DOCUMENT_CATEGORIES } from 'app/models/document/document-categories';
import { DocumentOwnerType, IDocumentOwner } from 'app/models/document/document';
import { DocumentService } from 'app/services/document/document.service';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

/** Maps the entity key the caller passes in onto the document owner, category and theme colour. */
const ENTITY_MAP: {
  [key: string]: {
    ownerType: DocumentOwnerType;
    category: string;
    color: string;
    descriptions: string[];
  }
} = {
  individual: {
    ownerType: 'PersonApplication',
    category: DOCUMENT_CATEGORIES.personApplication,
    color: 'individual',
    descriptions: ['Training Certificate', 'Passport Style Photo', 'Another State Current Certificate', 'Experience/Education']
  },
  firm: {
    ownerType: 'FirmApplication',
    category: DOCUMENT_CATEGORIES.firmApplication,
    color: 'firm',
    descriptions: ['Current Individual Training Certificate']
  },
  atpAudit: {
    ownerType: 'AtpAudit',
    category: DOCUMENT_CATEGORIES.atpAudit,
    color: 'atp',
    descriptions: []
  },
  atpAccreditation: {
    ownerType: 'AtpAccreditation',
    category: DOCUMENT_CATEGORIES.atpAccreditation,
    color: 'atp',
    descriptions: []
  }
};

@Component({
  selector: 'app-add-document',
  templateUrl: './add-document.component.html',
  styleUrls: ['./add-document.component.scss']
})
export class AddDocumentComponent implements OnInit {
  public uiData = LEAD_BASED_PAINT;
  public color = 'white';
  public fileDescription = new FormControl('');
  public documentData: any = { id: 0 };
  public fileName = '';
  public valid = false;
  public documentTypes: string[] = [];
  public filteredDocumentTypes: Observable<string[]>;
  public isProcessing = false;
  public acceptedFileTypes = ACCEPTED_FILE_TYPES.extensions.join(',');

  private selectedFile: File = null;

  constructor(
    private readonly dialogRef: MatDialogRef<AddDocumentComponent>,
    public dialog: MatDialog,
    private readonly documentService: DocumentService,
    private readonly toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: { document: any, entity: string, entityRef: any }
  ) {
    const entity = ENTITY_MAP[this.data.entity];

    if (entity) {
      this.color = this.uiData[entity.color].color;
      this.documentTypes = entity.descriptions;
    }

    if (this.data.document) {
      this.documentData = this.data.document;
      this.fileDescription.setValue(this.documentData.fileDescription);
      this.fileName = this.documentData.fileName;
      this.valid = true;
    }
  }

  ngOnInit(): void {
    this.filteredDocumentTypes = this.fileDescription.valueChanges.pipe(
      startWith(''),
      map(value => this.filterFileTypes(value))
    );
  }

  private filterFileTypes(value: string): string[] {
    const filterValue = (value ?? '').toLowerCase();
    return this.documentTypes.filter(option => option.toLowerCase().includes(filterValue));
  }

  onFileSelected(event: any): void {
    const file = (event.target as HTMLInputElement).files[0];

    if (!file) {
      return;
    }

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!ACCEPTED_FILE_TYPES.extensions.includes(extension)) {
      this.toastr.error(`Only the following file types are accepted: ${ACCEPTED_FILE_TYPES.displayText}`);
      return;
    }

    this.selectedFile = file;
    this.fileName = file.name;
    this.valid = true;
  }

  savePrompt(): void {
    const entity = ENTITY_MAP[this.data.entity];

    if (!entity || !this.selectedFile) {
      this.toastr.error('Select a document before saving.');
      return;
    }

    const owner: IDocumentOwner = {
      ownerType: entity.ownerType,
      ownerId: this.data.entityRef?.id
    };

    if (!owner.ownerId) {
      this.toastr.error('Save the record before adding documents to it.');
      return;
    }

    this.isProcessing = true;

    this.documentService
      .uploadDocument(this.selectedFile, owner, entity.category, this.fileDescription.value)
      .subscribe({
        next: () => {
          this.isProcessing = false;
          this.dialogRef.close(true);
        },
        error: error => {
          this.isProcessing = false;
          this.toastr.error(error?.error ?? 'An error occurred while uploading the document.');
        }
      });
  }
}

